import { useEffect, useState } from 'react';

import { send } from '@actual-app/core/platform/client/connection';
import type { EstadoPlan } from '@actual-app/core/server/rumbo/plan-app';

import { useSelector } from '#redux';
import { registrarUso } from '#uso';

import { useSyncServerStatus } from './useSyncServerStatus';

export type UsePlan = {
  plan: EstadoPlan | null;
  cargando: boolean;
  /** «Solo este móvil»: sin cuenta en el servidor no hay planes. */
  sinServidor: boolean;
  /**
   * Periodo terminado (o más de 7 días sin red): se puede mirar y exportar,
   * pero no apuntar ni sincronizar. Es la única respuesta a «¿tengo plan
   * activo?»: todo lo que cierre algo mira esto.
   */
  soloVer: boolean;
  quiereAviso: (tema: string) => boolean;
  pedirAviso: (tema: string, activo: boolean) => Promise<void>;
};

/**
 * Rumbo: el plan de la persona (estado, tramo y fechas) y los «Avísame» que
 * ha pedido. Sin red usa lo último que dijo el servidor (hasta 7 días, ver
 * `plan-mio` en loot-core).
 */
export function usePlan(): UsePlan {
  const status = useSyncServerStatus();
  const userData = useSelector(state => state.user.data);
  const sinServidor = !userData?.userId || Boolean(userData?.offline);
  const [plan, setPlan] = useState<EstadoPlan | null>(null);
  const [cargando, setCargando] = useState(!sinServidor);

  useEffect(() => {
    if (sinServidor) {
      setCargando(false);
      return;
    }
    let vigente = true;
    setCargando(true);
    send('plan-mio')
      .then(estado => {
        if (vigente) {
          setPlan(estado);
        }
      })
      .catch(() => {
        if (vigente) {
          setPlan(null);
        }
      })
      .finally(() => {
        if (vigente) {
          setCargando(false);
        }
      });
    return () => {
      vigente = false;
    };
  }, [status, sinServidor]);

  const quiereAviso = (tema: string) => plan?.avisame.includes(tema) ?? false;

  async function pedirAviso(tema: string, activo: boolean) {
    setCargando(true);
    try {
      const avisame = await send('plan-avisame', { tema, activo });
      if (avisame) {
        setPlan(actual => (actual ? { ...actual, avisame } : actual));
        registrarUso('plan_avisame', { tema, activo });
      }
    } finally {
      setCargando(false);
    }
  }

  const soloVer = esSoloVer(plan);

  return { plan, cargando, sinServidor, soloVer, quiereAviso, pedirAviso };
}

/** ¿Está en «solo ver»? Sin plan conocido (sin servidor) no se cierra nada. */
export function esSoloVer(plan: EstadoPlan | null): boolean {
  return plan?.estado === 'caducado';
}
